<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="document-main">
    <div>
      <Menus :menu="menu" @menuClicked="handleMenuClick" />
    </div>
    <div class="table-box-container">
      <TableBox
        title="Document"
        :tableData="documentListMaster"
        :maxSelection="1"
        :selectedRow.sync="documentTableBoxSelectedRow"
        :loading="documentTableBoxLoading"
      />
    </div>

    <PopupEntry
      class="popup-entry-component"
      :show.sync="showPopupEntry"
      v-if="showPopupEntry"
      :title="popupEntryTitle"
      :updatedBy="updatedBy"
      :createdBy="createdBy"
      @save="popupEntrySaveClicked"
      :requiredError="popupEntryRequiredError"
      :successMessages="popupEntrySuccessMessages"
      :messages="popupEntryMessages"
      :errorMessages="popupEntryErrorMessages"
      @requiredClicked="popupEntryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div class="general-input">
        <div class="document-code-and-desc">
          <Input
            class="document-code-input"
            placeholder="Document Code"
            :inputValue.sync="documentCodeInputValue"
            :isRequired="true"
            :isDisabled="viewModePK"
            :errorMessage.sync="documentCodeErr"
            ref="documentCodeErr"
            validator="masterfilecode,10,uppercase"
          />
          <Input
            placeholder="Description"
            :inputValue.sync="description"
            :isRequired="true"
            :errorMessage.sync="descriptionErr"
            :isDisabled="viewMode"
            validator="text,50"
            ref="descriptionErr"
          />
        </div>
        <div class="update-sets">
          <CheckBoxV2
            class="update-sets-checkbox"
            title="Update # of Sets?"
            :isChecked.sync="updateSetsIsChecked"
            :isDisabled="viewMode"
            ref="updateSetsCheckbox"
          />
        </div>
        <div class="document-status-group">
          <InputQuery
            placeholder="Received Copy Status"
            :isDisabled="viewMode"
            :items="statusCodeList"
            :allowFreeText="false"
            :selectedObject.sync="receivedCopyStatusCode"
            itemKey="displayText"
            validator="text,108"
          />
          <InputQuery
            placeholder="Received Original Status"
            :isDisabled="viewMode"
            :items="statusCodeList"
            :allowFreeText="false"
            :selectedObject.sync="receivedOriginalStatusCode"
            itemKey="displayText"
            validator="text,108"
          />
          <InputQuery
            placeholder="Prepared Status"
            :isDisabled="viewMode"
            :items="statusCodeList"
            :allowFreeText="false"
            :selectedObject.sync="preparedStatusCode"
            itemKey="displayText"
            validator="text,108"
          />
          <InputQuery
            placeholder="Submitted Status"
            :isDisabled="viewMode"
            :items="statusCodeList"
            :allowFreeText="false"
            :selectedObject.sync="submittedStatusCode"
            itemKey="displayText"
            validator="text,108"
          />
          <InputQuery
            placeholder="Couriered Copy Status"
            :isDisabled="viewMode"
            :items="statusCodeList"
            :allowFreeText="false"
            :selectedObject.sync="courieredCopyStatusCode"
            itemKey="displayText"
            validator="text,108"
          />
        </div>
      </div>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {},
  data: function () {
    return {
      documentListMaster: [],
      statusCodeList: [],

      menu: [
        {
          title: 'New Document',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit Document',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete Document',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: false,
          enabled: false,
        },
        {
          title: 'View Document',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuQuery',
          visible: true,
          enabled: false,
        },
      ],

      documentTableBoxSelectedRow: [],
      documentTableBoxLoading: false,

      // popup entry state
      showPopupEntry: false,
      updatedBy: '',
      createdBy: '',
      viewMode: false,
      viewModePK: false,
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryMessages: [],
      popupEntryErrorMessages: [],

      // popup entry common fields
      documentCodeInputValue: '',
      documentCodeErr: '',
      description: '',
      descriptionErr: '',
      updateSetsIsChecked: null,
      receivedCopyStatusCode: null,
      receivedOriginalStatusCode: null,
      preparedStatusCode: null,
      submittedStatusCode: null,
      courieredCopyStatusCode: null,
    };
  },
  props: {},
  methods: {
    getDocumentListMaster: async function () {
      this.documentTableBoxLoading = true;
      this.documentListMaster = [];
      this.documentTableBoxSelectedRow = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETDOCNEW' });
      this.documentListMaster = result;
      this.documentTableBoxLoading = false;
    },

    getDocumentDetails: async function (docCode) {
      return (await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'DOC', recordid: docCode }))['Doc'];
    },

    handleMenuClick: async function (menu) {
      this.clearPopupEntryMessages();
      this.resetPopupEntryFields();

      switch (menu.title) {
        case 'New Document':
          this.popupEntryTitle = 'New Document';
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;

          this.$nextTick(() => {
            this.$refs.documentCodeErr.setFocus(); // Set focus on the first editable field in new
          });

          break;

        case 'Edit Document':
          if (this.documentTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No document is selected', 'Please select a document to edit');
            break;
          }

          this.$fullscreenLoading('Getting Document Details');
          this.populatePopupEntryFields(await this.getDocumentDetails(this.documentTableBoxSelectedRow[0]['Doc Code']));
          this.$fullscreenLoading();

          this.viewMode = false;
          this.viewModePK = true;
          this.showPopupEntry = true;
          this.popupEntryTitle = 'Edit Document';

          this.$nextTick(() => {
            this.$refs.descriptionErr.setFocus(); // Set focus on the first editable field in edit
          });

          break;

        case 'Delete Document': {
          if (this.documentTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No document is selected', 'Please select a document to delete');
            break;
          }

          this.$fullscreenLoading('Getting Document Details');
          this.populatePopupEntryFields(await this.getDocumentDetails(this.documentTableBoxSelectedRow[0]['Doc Code']));
          this.$fullscreenLoading();

          const promptMessage = `Are you sure you want to delete Document? <b>${this.documentTableBoxSelectedRow[0]['Doc Code']}</b>?`;
          const deleteConfirmation = await this.$showAlert('warning', 'Delete Document', promptMessage, [
            { text: 'Delete', textColor: 'white', color: 'red' },
            { text: 'Cancel' },
          ]);
          if (deleteConfirmation == 'Cancel') {
            break;
          }
          this.$fullscreenLoading('Deleting Document');
          const result = await this.$apiCallV2('/Document/SaveDoc', this.formatSaveDocumentCodeRequestBody('D'));
          this.$fullscreenLoading();

          if (result[0]['Results'] != '0') {
            // Delete error
            this.$showAlert(
              'error',
              'Document NOT Deleted',
              `There was an error deleting Document ${this.documentTableBoxSelectedRow[0]['Doc Code']}`,
            );
            break;
          }

          // Delete successful
          this.$showAlert(
            'success',
            'Document Deleted',
            `Successfully deleted Document ${this.documentTableBoxSelectedRow[0]['Doc Code']}`,
          );
          this.documentTableBoxSelectedRow = [];
          this.getDocumentListMaster();
          break;
        }

        case 'View Document':
          if (this.documentTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No document is selected', 'Please select a document to view');
            break;
          }

          this.$fullscreenLoading('Getting Document Details');
          this.populatePopupEntryFields(await this.getDocumentDetails(this.documentTableBoxSelectedRow[0]['Doc Code']));
          this.$fullscreenLoading();

          this.popupEntryTitle = 'View Document';
          this.viewMode = true;
          this.viewModePK = true;
          this.showPopupEntry = true;
          break;
      }
    },

    clearPopupEntryMessages: function () {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryMessages = [];
      this.popupEntryErrorMessages = [];
    },
    resetPopupEntryFields: function () {
      // clears off most fields, and set defaults for some
      // popup entry state
      this.popupEntryTitle = '';
      this.documentCodeInputValue = '';
      this.documentCodeErr = '';
      this.description = '';
      this.descriptionErr = '';
      this.updateSetsIsChecked = null;
      this.receivedCopyStatusCode = null;
      this.receivedOriginalStatusCode = null;
      this.preparedStatusCode = null;
      this.submittedStatusCode = null;
      this.courieredCopyStatusCode = null;
    },

    populatePopupEntryFields: function (documentDetails) {
      this.documentCodeInputValue = this.populateTextTrim(documentDetails['Doc_Code']);
      this.description = this.populateTextTrim(documentDetails['Document_Description']);
      this.updateSetsIsChecked = documentDetails['Sets'] == '1';
      this.receivedCopyStatusCode =
        documentDetails['Copy_Received'] ?
          this.populateObject(this.statusCodeList, 'Status', documentDetails['Copy_Received'])
        : null;
      this.receivedOriginalStatusCode =
        documentDetails['Original_Received'] ?
          this.populateObject(this.statusCodeList, 'Status', documentDetails['Original_Received'])
        : null;
      this.preparedStatusCode =
        documentDetails['Prepared'] ?
          this.populateObject(this.statusCodeList, 'Status', documentDetails['Prepared'])
        : null;
      this.submittedStatusCode =
        documentDetails['Submitted'] ?
          this.populateObject(this.statusCodeList, 'Status', documentDetails['Submitted'])
        : null;
      this.courieredCopyStatusCode =
        documentDetails['Couriered'] ?
          this.populateObject(this.statusCodeList, 'Status', documentDetails['Couriered'])
        : null;
    },

    checkPopupEntryFields: function () {
      let error = false;

      this.clearPopupEntryMessages();
      if (this.documentCodeInputValue.trim() === '') {
        this.documentCodeErr = 'Document Code is required!';
        this.popupEntryRequiredError.push({ key: 'documentCodeErr', value: 'Document Code is required!' });
        error = true;
      } else if (this.documentCodeInputValue.length > 10) {
        this.documentCodeErr = 'Document Code cannot exceed 10 characters!';
        this.popupEntryRequiredError.push({
          key: 'documentCodeErr',
          value: 'Document Code cannot exceed 10 characters!',
        });
        error = true;
      }

      if (this.description.trim() === '') {
        this.descriptionErr = 'Description is required!';
        this.popupEntryRequiredError.push({ key: 'descriptionErr', value: 'Description is required!' });
        error = true;
      } else if (this.description.length > 50) {
        this.descriptionErr = 'Description cannot exceed 50 characters!';
        this.popupEntryRequiredError.push({
          key: 'descriptionErr',
          value: 'Description cannot exceed 50 characters!',
        });
        error = true;
      }

      const statusCodes = [
        { code: this.receivedCopyStatusCode?.['Status'], ref: 'Received Copy Status' },
        { code: this.receivedOriginalStatusCode?.['Status'], ref: 'Received Original Status' },
        { code: this.preparedStatusCode?.['Status'], ref: 'Prepared Status' },
        { code: this.submittedStatusCode?.['Status'], ref: 'Submitted Status' },
        { code: this.courieredCopyStatusCode?.['Status'], ref: 'Couriered Copy Status' },
      ];

      const statusCodeRefs = {};

      statusCodes.forEach((status) => {
        if (status.code) {
          if (statusCodeRefs[status.code]) {
            statusCodeRefs[status.code].push(status.ref);
          } else {
            statusCodeRefs[status.code] = [status.ref];
          }
        }
      });

      for (const code in statusCodeRefs) {
        if (statusCodeRefs[code].length > 1) {
          this.popupEntryErrorMessages.push(
            `${statusCodeRefs[code].join(', ')} have duplicated status code of: ${code}`,
          );
          error = true;
        }
      }

      return error;
    },

    formatSaveDocumentCodeRequestBody: function (action) {
      const requestBody = {
        action: action,
        documentcode: this.documentCodeInputValue.trim(),
        sets: this.updateSetsIsChecked ? '1' : '0',
        documentdescription: this.description,
        copyreceived: this.receivedCopyStatusCode?.['Status'] ?? '',
        originalreceived: this.receivedOriginalStatusCode?.['Status'] ?? '',
        prepared: this.preparedStatusCode?.['Status'] ?? '',
        submitted: this.submittedStatusCode?.['Status'] ?? '',
        couriered: this.courieredCopyStatusCode?.['Status'] ?? '',
      };
      return requestBody;
    },

    popupEntrySaveClicked: async function () {
      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }
      const action = this.popupEntryTitle[0]; // first character
      const requestBody = this.formatSaveDocumentCodeRequestBody(action);
      this.$fullscreenLoading('Saving Document Code');
      const result = await this.$apiCallV2('/Document/SaveDoc', requestBody);
      if (result[0]['Results'] != '0') {
        // Save error
        if (action == 'N') {
          this.popupEntryErrorMessages.push(`There was an error creating Document Code: ${result[0]['ErrMsg']}`);
        } else {
          this.popupEntryErrorMessages.push(`There was an error editing Document Code: ${result[0]['ErrMsg']}`);
        }
        this.$fullscreenLoading();
        return;
      }
      // Save successful
      this.viewMode = true;
      this.viewModePK = true;
      this.popupEntryTitle = 'View Document Code';
      this.resetPopupEntryFields();
      this.$fullscreenLoading('Refreshing Document Code');
      await this.getDocumentListMaster(); // refresh Document Code table
      this.$fullscreenLoading('Getting Document Code Details');
      const documentDetails = await this.getDocumentDetails(requestBody.documentcode);
      this.populatePopupEntryFields(documentDetails);
      if (action == 'N') {
        this.popupEntrySuccessMessages.push(`Successfully created Document Code: ${this.documentCodeInputValue}`);
      } else if (action == 'E' && this.popupEntryErrorMessages.length === 0) {
        this.popupEntrySuccessMessages.push(`Successfully edited Document Code: ${this.documentCodeInputValue}`);
      }
      this.$fullscreenLoading();
    },

    popupEntryRequiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    populateTextTrim: function (text) {
      // To populate text with trim
      return text ? text.trim() : '';
    },

    populateObject: function (data, key, compare) {
      // To populate object into dropdown or input query
      try {
        return data.find((d) => (d[key]?.trim?.() ?? d[key]) == (compare?.trim?.() ?? compare));
      } catch (error) {
        return null;
      }
    },
  },

  mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Document') {
        x.data.forEach((k) => {
          // Key in menu same as control value from API
          const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            if (k.Properties === 'Visible' || k.Properties === 'Enabled') {
              selectedMenu['visible'] = selectedMenu['enabled'] = k['PropertiesValue'] === 'True';
            }
          }
        });
      }
    });

    const mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.getDocumentListMaster().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STATUS' }).then((result) => {
        this.statusCodeList = result.map((item) => ({
          ...item,
          displayText: `${item['Status']} - ${item['Status Desc']}`,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    Promise.allSettled(mountedPromises).then((_promiseOutcomes) => {
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.document-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .table-box-container {
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    background-color: white;
    border-radius: 5px;
    display: flex;
    overflow: hidden;
  }

  .document-code-and-desc {
    display: flex;
    flex-direction: column;
    width: 100%;

    .document-code-input {
      width: 100%;
    }
  }
}
</style>
